import React, { useEffect, useRef, ReactNode, useId } from 'react';
import { X } from 'phosphor-react';
import { cn } from '../utils/cn';
import { Button } from './Button';

export interface CenterPeekModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  title?: string;
  description?: string;
  showCloseButton?: boolean;
  closeOnBackdropClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
  headerClassName?: string;
  footerClassName?: string;
  bodyClassName?: string;
  headerBackground?: string;
  bodyBackground?: string;
  footerBackground?: string;
  /** `viewport`: caja del modal al 90% del viewport en ancho y alto (90vw × 90vh), deja aire a los lados como el alto */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'viewport';
  /** Optional node rendered between the title/header area and the close button */
  headerExtra?: ReactNode;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

export const CenterPeekModal: React.FC<CenterPeekModalProps> = ({
  isOpen,
  onClose,
  children,
  header,
  footer,
  title,
  description,
  showCloseButton = true,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  className = '',
  overlayClassName = '',
  contentClassName = '',
  headerClassName = '',
  footerClassName = '',
  bodyClassName = '',
  headerBackground = 'bg-muted',
  bodyBackground = 'bg-muted',
  footerBackground = 'bg-muted',
  size = 'md',
  headerExtra,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const titleIdHook = useId();
  const descriptionIdHook = useId();

  // Size variants
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-7xl',
    full: 'max-w-full',
  };

  // Block body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store the previous active element for focus restoration
      previousActiveElementRef.current = document.activeElement as HTMLElement;
      
      // Block scroll on body
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // Handle Escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Focus trap
  useEffect(() => {
    if (!isOpen || !modalRef.current) return;

    const modal = modalRef.current;
    const focusableElements = modal.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // Focus automático desactivado - el modal no aplica focus al abrirse
    // El focus trap sigue funcionando para navegación con Tab

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    modal.addEventListener('keydown', handleTabKey);
    return () => modal.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  // Restore focus when modal closes
  useEffect(() => {
    if (!isOpen && previousActiveElementRef.current) {
      previousActiveElementRef.current.focus();
    }
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  // Generate unique IDs for accessibility
  const titleId = ariaLabelledBy || (title ? `modal-title-${titleIdHook}` : undefined);
  const descriptionId = ariaDescribedBy || (description ? `modal-description-${descriptionIdHook}` : undefined);

  return (
    <>
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className={cn('fixed inset-0 bg-black/50 z-50', overlayClassName)}
            onClick={handleBackdropClick}
            aria-hidden="true"
          />

          {/* Modal Container */}
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
            onClick={handleBackdropClick}
          >
            <div
              ref={modalRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              aria-describedby={descriptionId}
              className={cn(
                size === 'viewport'
                  ? 'w-[90vw] max-w-[90vw] shrink-0 h-[90vh] max-h-[90vh]'
                  : cn('w-full', sizeClasses[size], 'h-[90vh]', 'max-h-[90vh]'),
                bodyBackground, // Usa el mismo background que el body para el contenedor principal
                'rounded-xl',
                'overflow-hidden',
                'shadow-lg',
                'flex',
                'flex-col',
                'pointer-events-auto',
                className
              )}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              {(header || title || showCloseButton) && (
                <div
                  className={cn(
                    'flex',
                    'items-center',
                    'justify-between',
                    'px-3',
                    'py-2',
                    'border-b',
                    'border-border',
                    'shrink-0',
                    headerBackground,
                    headerClassName // Puede sobrescribir headerBackground si tiene bg-*
                  )}
                >
                  <div className="flex-1 min-w-0">
                    {header ? (
                      header
                    ) : (
                      <>
                        {title && (
                          <h2
                            id={titleId}
                            className="font-sans font-semibold text-lg text-foreground tracking-[-0.18px]"
                          >
                            {title}
                          </h2>
                        )}
                        {description && (
                          <p
                            id={descriptionId}
                            className="font-sans font-normal text-sm text-text-subtle mt-1"
                          >
                            {description}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                  {headerExtra && (
                    <div className="flex items-center shrink-0 ml-4">
                      {headerExtra}
                    </div>
                  )}
                  {showCloseButton && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={onClose}
                      className="ml-2"
                      aria-label="Cerrar modal"
                    >
                      <X size={20} weight="bold" />
                    </Button>
                  )}
                </div>
              )}

              {/* Body - Scrollable Content */}
              <div
                className={cn(
                  'flex-1',
                  'overflow-hidden',
                  'min-h-0',
                  'flex',
                  'flex-col',
                  bodyBackground,
                  bodyClassName // Puede sobrescribir bodyBackground si tiene bg-*
                )}
              >
                <div className={cn('h-full', 'flex', 'flex-col', 'min-h-0', contentClassName)}>
                  {children}
                </div>
              </div>

              {/* Footer - Solo se renderiza si se pasa la prop footer */}
              {footer && (
                <div
                  className={cn(
                    'flex',
                    'items-center',
                    'justify-end',
                    'gap-3',
                    'px-6',
                    'py-4',
                    'border-t',
                    'border-border',
                    'shrink-0',
                    footerBackground,
                    footerClassName // Puede sobrescribir footerBackground si tiene bg-*
                  )}
                >
                  {footer}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CenterPeekModal;

